/**
 * The small components that carry a rule rather than a style.
 *
 * Each one exists because of something in the stop list or the five rules:
 * a blocked action that stays on screen with its rule, an unverified statutory
 * marker that may not be tidied away, a skipped document that keeps its reason,
 * a period that speaks in one voice everywhere.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiFailure, api } from '../api/client'
import type { ClockView, TransitionOption } from '../api/client'
import { readDate, readPeriod } from '../lib/period'
import type { PeriodInput } from '../lib/period'

/* ------------------------------------------------------------------ period */

export function ClockPill({ from, title }: { from: PeriodInput | null; title?: string }) {
  const period = readPeriod(from)
  return (
    <span className={`chip ${period.tone}`} title={title ?? period.sentence}>
      {period.short}
    </span>
  )
}

/** The queue cell. Pill plus the sentence, because a pill alone cannot name an act. */
export function PeriodCell({
  from,
  label,
}: {
  from: PeriodInput | null
  label?: string | null
}) {
  const period = readPeriod(from)
  return (
    <div>
      <ClockPill from={from} />
      {label ? <span className="small muted"> {label}</span> : null}
      <div className="small muted" style={{ marginTop: 2 }}>
        {period.sentence}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ document state */

const STATE_TONE: Record<string, string> = {
  done: 'good',
  current: 'accent',
  pending: 'neutral',
  awaited: 'warn',
  skipped: 'neutral',
  'out-of-scope': 'neutral',
}

const STATE_WORDS: Record<string, string> = {
  done: 'Done',
  current: 'Current',
  pending: 'Pending',
  awaited: 'Awaited',
  skipped: 'Skipped',
  'out-of-scope': 'Out of scope',
}

export function StateChip({ state }: { state: string }) {
  return (
    <span className={`chip ${STATE_TONE[state] ?? 'neutral'}`}>
      {STATE_WORDS[state] ?? state}
    </span>
  )
}

/* --------------------------------------------------------------- citations */

/**
 * Everywhere a rule is named. Clicking opens the drawer with the stored text —
 * and **the unverified marker is prominent when `verified` is false**. Nothing
 * seeded has been checked against a print copy of the rules, and that marker may
 * not be removed to tidy a screen (stop list 10).
 */
export function CitationChip({
  citation,
  ruleRefs,
}: {
  citation: string | null | undefined
  ruleRefs?: string[] | null
}) {
  const [open, setOpen] = useState(false)
  if (!citation) return null
  return (
    <>
      {/* The office's short form on the chip; the qualified key behind it. */}
      <button type="button" className="citation" onClick={() => setOpen(true)}>
        {citation}
      </button>
      {open ? (
        <RuleDrawer citation={citation} refs={ruleRefs ?? []} onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}

export function RuleDrawer({
  citation,
  refs,
  onClose,
}: {
  citation: string
  refs: string[]
  onClose: () => void
}) {
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={`Rule ${citation}`}>
        <header>
          <div>
            <div className="mono">{citation}</div>
            <div className="small muted">
              {refs.length ? refs.join(' · ') : 'not qualified to a corpus key'}
            </div>
          </div>
          <button type="button" className="quiet" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="body">
          {refs.length === 0 ? (
            <>
              <p>
                <span className="unverified">NO PROVISION NAMED</span>
              </p>
              <p className="small muted">
                This citation names an instrument rather than a provision, or an office
                memorandum the corpus does not hold, or it came out of the extraction malformed.
                There is nothing to look up, and inventing a key would make it look as though
                there were. The load report lists every one of these.
              </p>
            </>
          ) : (
            refs.map((ref) => <RuleText key={ref} ruleRef={ref} />)
          )}
        </div>
      </aside>
    </>
  )
}

/** One provision, as the corpus holds it — or an honest account of why not. */
function RuleText({ ruleRef }: { ruleRef: string }) {
  const rule = useQuery({ queryKey: ['rule', ruleRef], queryFn: () => api.getRule(ruleRef) })
  const notHeld = rule.error instanceof ApiFailure && rule.error.status === 404

  return (
    <section style={{ marginBottom: 20 }}>
      <h3 className="mono">{ruleRef}</h3>
      {rule.isLoading ? <p className="muted">Reading the corpus…</p> : null}
      {notHeld ? (
        <p className="small muted">
          A real provision the rule corpus has not been given. The citation is right; the text
          behind it has not been loaded. That gap is counted per template in the load report.
        </p>
      ) : null}
      {rule.error && !notHeld ? <ErrorEnvelope error={rule.error} /> : null}
      {rule.data ? (
        <>
          <p className="small muted">{rule.data.title_en ?? ''}</p>
          {!rule.data.verified ? (
            <p>
              <span className="unverified">UNVERIFIED</span>{' '}
              <span className="small muted">
                Not yet checked against an official Hindi publication of the rules. This marker
                travels onto every document generated from it and is not removed to tidy a
                screen.
              </span>
            </p>
          ) : null}
          {rule.data.text_hi ? (
            <div className="rule-text">{rule.data.text_hi}</div>
          ) : (
            <p className="muted">
              No Hindi text is on record for this provision.
              {rule.data.note ? ` ${rule.data.note}` : ''}
            </p>
          )}
          {rule.data.text_en ? (
            <>
              <p className="small muted" style={{ marginTop: 10 }}>
                English reading — reference only; Hindi is the document of record.
              </p>
              <p>{rule.data.text_en}</p>
            </>
          ) : null}
          <p className="small faint">
            {rule.data.source_doc ?? 'source not recorded'}
            {rule.data.page_ref ? ` · ${rule.data.page_ref}` : ''} · extraction:{' '}
            {rule.data.extraction}
          </p>
        </>
      ) : null}
    </section>
  )
}

/* ------------------------------------------------------------ the envelope */

/**
 * The server's error, rendered as the server wrote it: the sentence, the rule,
 * the citation, and everything that is missing. A status code on screen tells
 * the officer nothing they can act on.
 */
export function ErrorEnvelope({ error }: { error: unknown }) {
  if (!error) return null
  if (!(error instanceof ApiFailure)) {
    return (
      <div className="envelope" role="alert">
        {error instanceof Error ? error.message : 'The request failed.'}
      </div>
    )
  }
  const body = error.body
  // **Never an empty box.** `body.error` is absent whenever the response is not
  // one of this API's envelopes — a 500 carrying FastAPI's own `{detail}`, a
  // proxy's HTML error page, a gateway timeout. Rendering that as a blank red
  // rectangle tells the officer that something failed and nothing about what,
  // which is the one thing rule 3 forbids an error to do. It cost a real
  // afternoon: the accounts screen showed two blank boxes and the actual cause
  // was a database one migration behind.
  const sentence =
    body.error ||
    (typeof body.detail === 'string' && body.detail) ||
    `The server answered ${error.status} and gave no reason. This is a fault, not a refusal — the request was not carried out.`
  return (
    <div className="envelope" role="alert">
      <div>{sentence}</div>
      {body.rule || body.citation ? (
        <div className="rule">
          {body.rule ?? ''}
          {body.rule && body.citation ? ' · ' : ''}
          {body.citation ?? ''}
        </div>
      ) : null}
      {body.missing && body.missing.length ? (
        <ul>
          {body.missing.map((item) => (
            <li key={item.slot} className="small">
              <span className="mono">{item.slot}</span>
              {item.reason ? ` — ${item.reason}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------- blocked, on screen */

/**
 * A blocked action **stays on screen, greyed, with the rule**. It is never
 * hidden and never silently removed: a button that vanishes teaches the officer
 * nothing about what would bring it back.
 */
export function BlockedAction({ option }: { option: TransitionOption }) {
  return (
    <div className="blocked">
      <button type="button" disabled>
        {option.label}
      </button>
      <div>
        <div className="why">{option.reason ?? 'Blocked.'}</div>
        {option.rule || option.citation ? (
          <div className="small faint mono">
            {option.rule ?? ''}
            {option.rule && option.citation ? ' · ' : ''}
            {option.citation ?? ''}
          </div>
        ) : null}
        {option.missing?.length ? (
          <div className="missing">needs: {option.missing.join(', ')}</div>
        ) : null}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- the clocks */

export function ClockRow({ clock }: { clock: ClockView }) {
  const period = readPeriod(clock)
  const extensions = clock.extensions ?? []
  return (
    <div className="clock">
      <div className="head">
        <strong>{clock.label}</strong>
        <ClockPill from={clock} />
        <span className={`chip ${clock.authority === 'Statutory' ? 'accent' : 'neutral'}`}>
          {clock.authority}
        </span>
        <CitationChip citation={clock.citation} ruleRefs={clock.rule_refs} />
      </div>
      <div className="sentence">{period.sentence}</div>
      <div className="source">{clock.source}</div>
      <div className="small muted" style={{ marginTop: 4 }}>
        {clock.started_on ? `Started ${readDate(clock.started_on)}` : 'Not started'}
        {clock.due_on ? ` · due ${readDate(clock.due_on)}` : ''}
        {clock.extension_headroom > 0
          ? ` · ${clock.extension_headroom} days of extension may still be granted`
          : ''}
      </div>
      {extensions.length ? (
        <ul className="ledger">
          {extensions.map((entry, index) => (
            <li key={index}>
              {String(entry['days'] ?? '?')} days —{' '}
              {String(entry['reason'] ?? 'no reason recorded')}
              {entry['granted_on'] ? ` · ${readDate(String(entry['granted_on']))}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
